/**
 * Old links to the in-app design-system gallery.
 *
 * The gallery (`/design-system`, `/settings/component-gallery` and `/admin/component-gallery`) was removed on
 * 8 October 2026. Ricoh Paper (`@ricoh-paper/angular`) is the design system now, and its own docs site, the
 * component viewer, shows every component and theme. Bookmarks and links in old documents still point here, so
 * each old URL sends the browser on to that site.
 *
 * The viewer is the Cloud Run service `ricoh-paper-viewer` (rdx-platform-eu, europe-west1), deployed from
 * ricoh-paper's `cloudbuild/deploy-viewer.yaml` on every push to its main branch and served at the domain below.
 * The site is public, so these routes carry no `authGuard`.
 *
 * The desktop app opens the site in the system browser and stays on Home: replacing the webview's page would
 * leave the user on a docs site inside the app window with no way back.
 *
 * Framework-light, like `manage-redirect.ts`: it imports no component, so `app.routes.ts` can use it without
 * growing the initial bundle.
 */
import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';

import { isTauri, openExternal } from '../tauri-runtime';
import { OPEN_OTHER_APP } from './manage-redirect';

/** The Ricoh Paper docs site (the component viewer). */
export const PAPER_DOCS_URL = 'https://paper.ricohworkplace.com/';

/** The old gallery URLs, as route paths, each answered by {@link toPaperDocs}. */
export const RETIRED_GALLERY_PATHS = ['design-system', 'settings/component-gallery', 'admin/component-gallery'] as const;

/** Leaves the app for the Paper docs site, replacing the history entry so Back does not bounce through here. */
export const toPaperDocs: CanActivateFn = () => {
  if (isTauri) {
    void openExternal(PAPER_DOCS_URL);
    return inject(Router).createUrlTree(['/home']);
  }
  inject(OPEN_OTHER_APP)(PAPER_DOCS_URL);
  return false;
};
